import { forwardRef, useEffect, useMemo } from 'react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { FaxTypes } from '@frontend/api-fax';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { SchemaFaxService, SchemaIO } from '@frontend/schema';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  Button,
  contextFactory,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemProps,
  useAlert,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';
import { useFaxActions } from '../../../hooks/use-fax-actions';
import { useFaxNavigation } from '../../../hooks/use-fax-navigation';
import { useSelectedFaxShallowStore } from '../../../stores/selected-fax-store';
import { ForwardFaxModal } from '../../inbox/forward-fax-modal';

const [MenuContext, useMenuContext] = contextFactory<{ close: () => void }>();

type ArchiveAction = {
  type: 'Archive';
  params: SchemaIO<(typeof SchemaFaxService)['UpdateFaxArchiveStatus']>['input'];
};

type BlockSenderAction = {
  type: 'Block';
  params: SchemaIO<(typeof SchemaFaxService)['BlockContact']>['input'];
};

type UnblockSenderAction = {
  type: 'Unblock';
  params: SchemaIO<(typeof SchemaFaxService)['UnblockContact']>['input'];
};

type PreviewFaxAction = {
  type: 'Preview';
  params: { faxId: string; locationId: string; direction: Direction_Enum; status: Status_Enum };
};

type ForwardAction = {
  type: 'Forward';
  params: {
    open: () => void;
  };
};

type DeleteAction = {
  type: 'Delete';
  params: { faxId: string; locationId: string };
};

type MarkUnreadAction = {
  type: 'MarkUnread';
  params: SchemaIO<(typeof SchemaFaxService)['UpdateFaxes']>['input'];
};

type DownloadFaxAction = {
  type: 'Download';
  params: { faxId: string; locationId: string; direction: Direction_Enum; status: Status_Enum };
};

type DefinedContactActions =
  | BlockSenderAction
  | UnblockSenderAction
  | PreviewFaxAction
  | ForwardAction
  | MarkUnreadAction
  | DownloadFaxAction
  | DeleteAction
  | ArchiveAction;
type GetThreadHeaderActionsProps = {
  faxId: string;
  openForwardModal: () => void;
};

export const useThreadHeaderActions = ({ faxId, openForwardModal }: GetThreadHeaderActionsProps) => {
  const { selectedFax } = useSelectedFaxShallowStore('selectedFax');
  const faxDeleteAcl = hasSchemaACL(selectedFax.locationId, Permission.FAX_DELETE);

  const outer: DefinedContactActions[] = [
    {
      type: 'Preview',
      params: {
        faxId,
        locationId: selectedFax.locationId,
        direction: selectedFax.direction,
        status: selectedFax.status,
      },
    },
    {
      type: 'Download',
      params: {
        faxId,
        locationId: selectedFax.locationId,
        direction: selectedFax.direction,
        status: selectedFax.status,
      },
    },
    {
      type: 'Archive',
      params: {
        faxIds: [faxId],
        locationId: selectedFax.locationId,
        isArchived: !selectedFax.isArchived,
      },
    } satisfies ArchiveAction,
  ].filter(Boolean) as DefinedContactActions[];

  const inner: DefinedContactActions[] = [
    {
      type: 'Forward',
      params: {
        open: openForwardModal,
      },
    },
    selectedFax.direction === Direction_Enum.INBOUND
      ? ({
          type: 'MarkUnread',
          params: {
            hasBeenRead: false,
            locationId: selectedFax.locationId,
            faxIds: [faxId],
          },
        } satisfies MarkUnreadAction)
      : undefined,
    faxDeleteAcl
      ? ({
          type: 'Delete',
          params: {
            faxId,
            locationId: selectedFax.locationId,
          },
        } satisfies DeleteAction)
      : undefined,
    selectedFax.direction === Direction_Enum.INBOUND
      ? selectedFax.isBlocked
        ? ({
            type: 'Unblock',
            params: {
              locationId: selectedFax.locationId,
              faxNumber: selectedFax.contactPhone,
              faxId: selectedFax.faxId,
            },
          } satisfies UnblockSenderAction)
        : ({
            type: 'Block',
            params: {
              locationId: selectedFax.locationId,
              faxNumber: selectedFax.contactPhone,
              faxId: selectedFax.faxId,
            },
          } satisfies BlockSenderAction)
      : undefined,
  ].filter(Boolean) as DefinedContactActions[];

  return { inner, outer };
};

const ArchiveActionButton = (props: ArchiveAction['params']) => {
  const { t } = useTranslation('fax');
  const { closeThread } = useFaxNavigation();
  const { archiveFax } = useFaxActions();

  return (
    <Button
      iconName={props.isArchived ? 'archive' : 'unarchive'}
      variant='secondary'
      size='small'
      trackingId={`${FaxPrefixes.Thread}-archive-action-button`}
      onClick={async () => {
        archiveFax(props.faxIds, props.locationId!, props.isArchived);
        closeThread();
      }}
      hoverLabel={props.isArchived ? t('Archive') : t('Unarchive')}
      css={{ border: `1px solid ${theme.colorsV2.black(20)}`, flexShrink: 0 }}
    />
  );
};

const PreviewFaxActionButton = (props: PreviewFaxAction['params']) => {
  const { t } = useTranslation('fax');
  const { previewFax, previewDeliveryReport, previewFaxAndDeliveryReport } = useFaxActions();

  const { getTriggerProps, getMenuProps, getItemProps, isOpen } = usePopoverMenu<HTMLButtonElement>({
    placement: 'bottom-start',
  });

  const isOutbound = props.direction === Direction_Enum.OUTBOUND && props.status === Status_Enum.DELIVERED;

  return (
    <div css={{ display: 'flex', alignItems: 'center' }}>
      <Button
        iconName='preview'
        variant='secondary'
        size='small'
        trackingId={`${FaxPrefixes.Thread}-preview-fax-action-button`}
        hoverLabel={t('Preview / Print')}
        onClick={() => previewFax(props.faxId, 'fax-pdf', props.locationId, true)}
        css={{
          border: `1px solid ${theme.colorsV2.black(20)}`,
          flexShrink: 0,
          borderTopRightRadius: isOutbound ? 0 : undefined,
          borderBottomRightRadius: isOutbound ? 0 : undefined,
          borderRight: isOutbound ? 'none' : undefined,
        }}
      />
      {isOutbound && (
        <>
          <Button
            variant='secondary'
            size='small'
            iconName={isOpen ? 'caret-up-small' : 'caret-down-small'}
            css={{
              border: `1px solid ${theme.colorsV2.black(20)}`,
              borderLeft: 'none',
              flexShrink: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              paddingLeft: theme.spacing(0.25),
              paddingRight: theme.spacing(0.25),
              minWidth: 'unset',
              width: 'auto',

              span: {
                minWidth: '12px',
              },

              'span > svg': {
                width: '12px',
                height: '12px',
              },
            }}
            {...getTriggerProps()}
          />
          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem
              {...getItemProps({ index: 0 })}
              onClick={() => previewFaxAndDeliveryReport(props.faxId, 'fax-preview', props.locationId)}
              trackingId={`${FaxPrefixes.Thread}-preview-fax-message-and-report-button`}
            >
              {t('Fax & Confirmation')}
            </PopoverMenuItem>
            <PopoverMenuItem
              {...getItemProps({ index: 1 })}
              onClick={() => previewFax(props.faxId, 'fax-pdf', props.locationId, true)}
              trackingId={`${FaxPrefixes.Thread}-preview-fax-message-button`}
            >
              {t('Fax Message Only')}
            </PopoverMenuItem>
            <PopoverMenuItem
              {...getItemProps({ index: 2 })}
              onClick={() => previewDeliveryReport(props.faxId, props.locationId)}
              trackingId={`${FaxPrefixes.Thread}-preview-fax-report-button`}
            >
              {t('Fax Confirmation Only')}
            </PopoverMenuItem>
          </PopoverMenu>
        </>
      )}
    </div>
  );
};

const DownloadFaxActionButton = (props: DownloadFaxAction['params']) => {
  const { t } = useTranslation('fax');
  const { downloadFax, downloadFaxDeliveryReport, downloadFaxAndDeliveryReport } = useFaxActions();

  const { getTriggerProps, getMenuProps, getItemProps, isOpen } = usePopoverMenu<HTMLButtonElement>({
    placement: 'bottom-start',
  });

  const isOutbound = props.direction === Direction_Enum.OUTBOUND && props.status === Status_Enum.DELIVERED;

  return (
    <div css={{ display: 'flex', alignItems: 'center' }}>
      <Button
        iconName='download'
        variant='secondary'
        size='small'
        trackingId={`${FaxPrefixes.Thread}-download-fax-action-button`}
        hoverLabel={t('Download')}
        onClick={() => downloadFax(props.faxId, `fax-${props.faxId}-pdf`, props.locationId, true)}
        css={{
          border: `1px solid ${theme.colorsV2.black(20)}`,
          flexShrink: 0,
          borderTopRightRadius: isOutbound ? 0 : undefined,
          borderBottomRightRadius: isOutbound ? 0 : undefined,
          borderRight: isOutbound ? 'none' : undefined,
        }}
      />
      {isOutbound && (
        <>
          <Button
            variant='secondary'
            size='small'
            iconName={isOpen ? 'caret-up-small' : 'caret-down-small'}
            css={{
              border: `1px solid ${theme.colorsV2.black(20)}`,
              borderLeft: 'none',
              flexShrink: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              paddingLeft: theme.spacing(0.25),
              paddingRight: theme.spacing(0.25),
              minWidth: 'unset',
              width: 'auto',
              span: {
                minWidth: '12px',
              },

              'span > svg': {
                width: '12px',
                height: '12px',
              },
            }}
            {...getTriggerProps()}
          />
          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem
              {...getItemProps({ index: 0 })}
              onClick={() =>
                downloadFaxAndDeliveryReport(props.faxId, `fax-${props.faxId}-pdf`, props.locationId, true)
              }
              trackingId={`${FaxPrefixes.Thread}-download-fax-message-and-report-button`}
            >
              {t('Fax & Confirmation')}
            </PopoverMenuItem>
            <PopoverMenuItem
              {...getItemProps({ index: 1 })}
              onClick={() => downloadFax(props.faxId, `fax-${props.faxId}-pdf`, props.locationId, true)}
              trackingId={`${FaxPrefixes.Thread}-download-fax-message-button`}
            >
              {t('Fax Message Only')}
            </PopoverMenuItem>
            <PopoverMenuItem
              {...getItemProps({ index: 2 })}
              onClick={() => downloadFaxDeliveryReport(props.faxId, props.locationId)}
              trackingId={`${FaxPrefixes.Thread}-download-fax-report-button`}
            >
              {t('Fax Confirmation Only')}
            </PopoverMenuItem>
          </PopoverMenu>
        </>
      )}
    </div>
  );
};

const BlockSenderMenuItem = forwardRef<
  HTMLButtonElement,
  { params: BlockSenderAction['params'] } & PopoverMenuItemProps<'button'>
>(({ params, ...rest }, ref) => {
  const { t } = useTranslation('fax');
  const { close } = useMenuContext();
  const alert = useAlert();
  const { closeThread } = useFaxNavigation();
  const { blockFax } = useFaxActions();

  return (
    <PopoverMenuItem
      {...rest}
      ref={ref}
      Icon={() => <Icon name='block' size={16} css={{ color: theme.colors.neutral70 }} />}
      onClick={async () => {
        try {
          if (!params.locationId || !params.faxNumber || !params.faxId) {
            throw Error;
          }
          await blockFax(params.locationId, params.faxNumber, params.faxId);
          closeThread();
          close();
        } catch (error) {
          alert.error('Error Blocking Contact');
        }
      }}
      trackingId={`${FaxPrefixes.Thread}-block-sender-button`}
    >
      {t('Block Sender')}
    </PopoverMenuItem>
  );
});

const UnblockSenderMenuItem = forwardRef<
  HTMLButtonElement,
  { params: BlockSenderAction['params'] } & PopoverMenuItemProps<'button'>
>(({ params, ...rest }, ref) => {
  const { t } = useTranslation('fax');
  const { close } = useMenuContext();
  const { closeThread } = useFaxNavigation();
  const { unblockFax } = useFaxActions();
  const alert = useAlert();

  return (
    <PopoverMenuItem
      {...rest}
      ref={ref}
      Icon={() => <Icon name='block' size={16} css={{ color: theme.colors.neutral70 }} />}
      onClick={async () => {
        try {
          if (!params.locationId || !params.faxNumber || !params.faxId) {
            throw Error;
          }
          await unblockFax(params.locationId, params.faxNumber, params.faxId);
          closeThread();
          close();
        } catch (error) {
          alert.error('Error Unblocking Contact');
        }
      }}
      trackingId={`${FaxPrefixes.Thread}-unblock-sender-button`}
    >
      {t('Unblock Sender')}
    </PopoverMenuItem>
  );
});

const ForwardFaxMenuItem = forwardRef<
  HTMLButtonElement,
  { params: ForwardAction['params'] } & PopoverMenuItemProps<'button'>
>(({ params, ...rest }, ref) => {
  const { t } = useTranslation('fax');
  const { selectedFax } = useSelectedFaxShallowStore('selectedFax');
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess();
  const canForwardFax =
    !selectedFax.locationPhone || checkUserLocationPhoneAccess(formatPhoneNumberE164(selectedFax.locationPhone));

  return (
    <PopoverMenuItem
      {...rest}
      ref={ref}
      Icon={() => <Icon name='forward' size={16} css={{ color: theme.colors.neutral70 }} />}
      disabled={!canForwardFax}
      hoverLabel={!canForwardFax ? t('You do not have access to this fax number') : undefined}
      onClick={canForwardFax ? params.open : undefined}
      trackingId={`${FaxPrefixes.Thread}-forward-fax-button`}
    >
      {t('Forward')}
    </PopoverMenuItem>
  );
});

const DeleteFaxMenuItem = forwardRef<
  HTMLButtonElement,
  { params: DeleteAction['params'] } & PopoverMenuItemProps<'button'>
>(({ params, ...rest }, ref) => {
  const { t } = useTranslation('fax');
  const { close } = useMenuContext();
  const { deleteFax } = useFaxActions();
  const { selectedFax } = useSelectedFaxShallowStore('selectedFax');

  return (
    <PopoverMenuItem
      {...rest}
      ref={ref}
      Icon={() => <Icon name='trash' size={16} css={{ color: theme.colors.neutral70 }} />}
      onClick={() => {
        const locationPhones = selectedFax.locationPhone ? [selectedFax.locationPhone] : [];
        deleteFax(params.locationId, [params.faxId], FaxTypes.InboxType.INBOX, locationPhones);
        close();
      }}
      trackingId={`${FaxPrefixes.Thread}-delete-fax-button`}
    >
      {t('Delete')}
    </PopoverMenuItem>
  );
});

const MarkUnreadMenuItem = forwardRef<
  HTMLButtonElement,
  { params: MarkUnreadAction['params'] } & PopoverMenuItemProps<'button'>
>(({ params, ...rest }, ref) => {
  const { t } = useTranslation('fax');
  const { close } = useMenuContext();
  const { closeThread } = useFaxNavigation();
  const { markReadFax } = useFaxActions();
  const { selectedFax } = useSelectedFaxShallowStore('selectedFax');

  return (
    <PopoverMenuItem
      {...rest}
      ref={ref}
      Icon={() => <Icon name='message-unread' size={16} css={{ color: theme.colors.neutral70 }} />}
      onClick={async () => {
        const locationPhones = selectedFax.locationPhone ? [selectedFax.locationPhone] : [];
        await markReadFax(!params.hasBeenRead, params.locationId!, params.faxIds, locationPhones);
        closeThread();
        close();
      }}
      trackingId={`${FaxPrefixes.Thread}-mark-unread-button`}
    >
      {t('Mark Unread')}
    </PopoverMenuItem>
  );
});

type ContactHeaderActionsProps = {
  faxId: string;
  locationId: string;
  isFaxRetryable: boolean;
};

const generateActionButtons = (actions: DefinedContactActions[]) => {
  return actions.map((action) => {
    switch (action.type) {
      case 'Preview':
        return <PreviewFaxActionButton key={action.type} {...action.params} />;
      case 'Download':
        return <DownloadFaxActionButton key={action.type} {...action.params} />;
      case 'Archive':
        return <ArchiveActionButton key={action.type} {...action.params} />;
      default:
        return null;
    }
  });
};

const generateActionMenuItems = (
  actions: DefinedContactActions[],
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps']
) => {
  return actions.map((action, index) => {
    const itemProps = getItemProps({ index });
    switch (action.type) {
      case 'Forward':
        return <ForwardFaxMenuItem key={action.type} params={action.params} {...itemProps} />;
      case 'MarkUnread':
        return <MarkUnreadMenuItem key={action.type} params={action.params} {...itemProps} />;
      case 'Delete':
        return <DeleteFaxMenuItem key={action.type} params={action.params} {...itemProps} />;
      case 'Block':
        return <BlockSenderMenuItem key={action.type} params={action.params} {...itemProps} />;
      case 'Unblock':
        return <UnblockSenderMenuItem key={action.type} params={action.params} {...itemProps} />;
      default:
        return null;
    }
  });
};

export const ThreadHeaderActions = ({ faxId, locationId, isFaxRetryable }: ContactHeaderActionsProps) => {
  const { t } = useTranslation('fax');
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({ placement: 'bottom' });

  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();

  const { modalProps: forwardModalProps, openModal: openForwardModal } = useModalControl();

  const actions = useThreadHeaderActions({ faxId, openForwardModal });

  useEffect(() => {
    if (forwardModalProps.show) {
      close();
    }
  }, [forwardModalProps.show]);

  const outerActions = useMemo(() => {
    return isFaxRetryable ? actions.outer.filter((action) => action.type !== 'Archive') : actions.outer;
  }, [isFaxRetryable, actions.outer]);

  return (
    <>
      <div style={{ display: 'flex', gap: theme.spacing(1) }}>
        {generateActionButtons(outerActions)}
        <Button
          iconName='more'
          variant='secondary'
          hoverLabel={t('More Actions')}
          css={{
            border: `1px solid ${theme.colorsV2.black(20)}`,
          }}
          {...triggerProps}
          trackingId={`${FaxPrefixes.Thread}-more-action-button`}
        />
      </div>
      <MenuContext.Provider value={{ close }}>
        <PopoverMenu {...menuProps}>{generateActionMenuItems(actions.inner, getItemProps)}</PopoverMenu>
      </MenuContext.Provider>
      {forwardModalProps.show && (
        <ForwardFaxModal locationId={locationId} faxId={faxId} forwardModalProps={forwardModalProps} />
      )}
    </>
  );
};
